import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Minus, Trash2, ShoppingBag } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import TopBar from '../components/common/TopBar';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const Cart = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { cart, kiosk, updateQuantity, removeItem, getTotal } = useCart();
  const { isAuthenticated } = useAuth();

  const handleCheckout = () => {
    if (!isAuthenticated) {
      toast.error('Você precisa fazer login para continuar');
      navigate('/login');
      return;
    }
    navigate('/checkout');
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-background" data-testid="empty-cart">
        <TopBar title={t('cart.title')} showBack />
        <div className="max-w-md mx-auto p-8 text-center">
          <ShoppingBag className="w-20 h-20 mx-auto text-muted-foreground mb-4" strokeWidth={1.5} />
          <h2 className="font-['Outfit'] text-xl font-bold text-foreground mb-2">
            {t('cart.empty')}
          </h2>
          <p className="text-muted-foreground mb-6">
            {t('cart.emptySub')}
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-full font-semibold hover:opacity-90 transition-opacity active:scale-95"
          >
            {t('cart.exploreKiosks')}
          </button>
        </div>
      </div>
    );
  }

  const total = getTotal();

  return (
    <div className="min-h-screen bg-background pb-32" data-testid="cart-page">
      <TopBar title={t('cart.title')} showBack />

      <div className="max-w-md mx-auto p-4">
        {kiosk && (
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 mb-4">
            <p className="text-xs text-muted-foreground">Pedido de:</p>
            <p className="font-semibold text-foreground">{kiosk.display_name}</p>
          </div>
        )}

        <div className="space-y-3">
          {cart.map((item) => (
            <div
              key={item.product_id}
              data-testid={`cart-item-${item.product_id}`}
              className="bg-white rounded-xl shadow-sm border border-border p-3 flex items-center gap-3"
            >
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-foreground">{item.product_name}</h3>
                <p className="text-primary font-bold mt-1">
                  R$ {item.subtotal.toFixed(2)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid={`decrease-${item.product_id}`}
                  onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                  className="w-8 h-8 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors active:scale-95"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-8 text-center font-bold">{item.quantity}</span>
                <button
                  data-testid={`increase-${item.product_id}`}
                  onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                  className="w-8 h-8 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors active:scale-95"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <button
                data-testid={`remove-${item.product_id}`}
                onClick={() => removeItem(item.product_id)}
                className="text-destructive p-2 hover:bg-destructive/10 rounded-full transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-border p-4 mt-6">
          <div className="flex justify-between mb-2">
            <span className="text-muted-foreground">{t('cart.subtotal')}</span>
            <span className="font-semibold">R$ {total.toFixed(2)}</span>
          </div>
          <div className="flex justify-between mb-2">
            <span className="text-muted-foreground">{t('cart.deliveryFee')}</span>
            <span className="font-semibold text-secondary">{t('cart.free')}</span>
          </div>
          <div className="border-t border-border pt-2 mt-2 flex justify-between">
            <span className="font-bold text-foreground">{t('cart.total')}</span>
            <span className="font-bold text-primary text-xl" data-testid="cart-total">
              R$ {total.toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 max-w-md mx-auto p-4 bg-white border-t border-border z-40">
        <button
          data-testid="checkout-button"
          onClick={handleCheckout}
          className="w-full py-3 bg-primary text-primary-foreground rounded-full font-semibold hover:opacity-90 transition-opacity active:scale-95 shadow-lg"
        >
          {t('cart.continueToPayment')}
        </button>
      </div>
    </div>
  );
};

export default Cart;
